/**
 * Consciousness Kinetic Visualization
 * Visualizes the equation K_c = m_c * v_c^2 / 2 from the Theory of Sovereign Reflectivity
 */



// Main p5.js sketch
const consciousnessKineticSketch = (p) => {
    // Configuration
    const config = {
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18], // #121212 (Near-black)
            text: [245, 245, 245], // #F5F5F5 (Off-white)
            lowVelocity: [52, 152, 219], // #3498DB (Blue)
            highVelocity: [231, 76, 60] // #E74C3C (Red)
        },
        showComparison: false
    };

    // Variables for the equation
    let consciousnessMass = 1.0; // Consciousness mass (m_c)
    let consciousnessVelocity = 0.5; // Consciousness velocity (v_c)
    let kineticEnergy = 0.125; // Kinetic energy (K_c) (calculated)

    // UI elements
    let massSlider, velocitySlider;
    let massValue, velocityValue, energyValue;
    let resetButton, toggleComparisonButton;

    // Animation variables
    let time = 0;
    let particles = [];
    let field = [];

    // Setup function
    p.setup = function () {
        // Create canvas
        const canvas = p.createCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
        const canvasContainer = document.getElementById('consciousness-kinetic-canvas');
        if (canvasContainer) {
            canvas.parent('consciousness-kinetic-canvas');
        } else {
            console.warn('Consciousness kinetic canvas container not found, visualization may not display correctly');
        }

        // Initialize UI elements from DOM
        massSlider = document.getElementById('physical-slider');
        velocitySlider = document.getElementById('nonphysical-slider');
        massValue = document.getElementById('physical-value');
        velocityValue = document.getElementById('nonphysical-value');
        energyValue = document.getElementById('kinetic-value');
        resetButton = document.getElementById('reset-button');
        toggleComparisonButton = document.getElementById('toggle-comparison');

        // Add event listeners
        if (massSlider) massSlider.addEventListener('input', updateMass);
        if (velocitySlider) velocitySlider.addEventListener('input', updateVelocity);
        if (resetButton) resetButton.addEventListener('click', resetValues);
        if (toggleComparisonButton) toggleComparisonButton.addEventListener('click', toggleComparison);

        // Initialize particles
        createParticles();

        // Initialize field
        createField();

        // Calculate initial kinetic energy
        calculateKineticEnergy();
    };

    // Draw function
    p.draw = function () {
        p.background(config.colors.background);

        if (config.showComparison) {
            drawComparisonView();
        } else {
            drawStandardView();
        }

        // Update animation time
        time += 0.02;
    };

    // Create particles for consciousness visualization
    function createParticles() {
        particles = [];
        const particleCount = 100;

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: p.random(p.width),
                y: p.random(p.height),
                size: p.random(3, 8) * consciousnessMass,
                speed: p.random(0.5, 2) * consciousnessVelocity,
                angle: p.random(p.TWO_PI),
                color: p.lerpColor(
                    p.color(config.colors.lowVelocity),
                    p.color(config.colors.highVelocity),
                    consciousnessVelocity
                )
            });
        }
    }

    // Create field for consciousness visualization
    function createField() {
        field = [];
        const gridSize = 20;
        const cols = Math.ceil(p.width / gridSize);
        const rows = Math.ceil(p.height / gridSize);

        for (let i = 0; i < cols; i++) {
            for (let j = 0; j < rows; j++) {
                field.push({
                    x: i * gridSize,
                    y: j * gridSize,
                    angle: p.random(p.TWO_PI),
                    strength: p.random(0.2, 1)
                });
            }
        }
    }

    // Update consciousness mass from slider
    function updateMass() {
        if (!massSlider) return;

        consciousnessMass = parseFloat(massSlider.value);
        if (massValue) {
            massValue.textContent = consciousnessMass.toFixed(1);
        }
        calculateKineticEnergy();

        // Update particle sizes
        for (let particle of particles) {
            particle.size = p.random(3, 8) * consciousnessMass;
        }
    }

    // Update consciousness velocity from slider
    function updateVelocity() {
        if (!velocitySlider) return;

        consciousnessVelocity = parseFloat(velocitySlider.value);
        if (velocityValue) {
            velocityValue.textContent = consciousnessVelocity.toFixed(2);
        }
        calculateKineticEnergy();

        // Update particle speeds and colors
        for (let particle of particles) {
            particle.speed = p.random(0.5, 2) * consciousnessVelocity;
            particle.color = p.lerpColor(
                p.color(config.colors.lowVelocity),
                p.color(config.colors.highVelocity),
                consciousnessVelocity
            );
        }
    }

    // Calculate kinetic energy based on K_c = m_c * v_c^2 / 2
    function calculateKineticEnergy() {
        kineticEnergy = consciousnessMass * (consciousnessVelocity * consciousnessVelocity) / 2;
        if (energyValue) {
            energyValue.textContent = kineticEnergy.toFixed(3);
        }
    }

    // Reset all values to defaults
    function resetValues() {
        if (!massSlider || !velocitySlider) return;

        massSlider.value = 1.0;
        velocitySlider.value = 0.5;
        updateMass();
        updateVelocity();
    }

    // Toggle comparison view
    function toggleComparison() {
        config.showComparison = !config.showComparison;
        if (toggleComparisonButton) {
            toggleComparisonButton.textContent = config.showComparison ? 'Hide Comparison' : 'Toggle Comparison';
        }
    }

    // Draw standard visualization view
    function drawStandardView() {
        // Draw background grid
        drawGrid();

        // Draw field
        drawField();

        // Draw consciousness particles
        drawParticles();

        // Draw labels and equation
        drawLabels();
    }

    // Draw field
    function drawField() {
        const fieldStrength = consciousnessVelocity * 15;

        for (let point of field) {
            const angle = point.angle + time * consciousnessVelocity;
            const strength = point.strength * fieldStrength;

            p.push();
            p.translate(point.x, point.y);
            p.rotate(angle);

            p.stroke(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 100);
            p.strokeWeight(1);
            p.line(0, 0, strength, 0);

            p.noStroke();
            p.fill(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 150);
            p.ellipse(strength, 0, 2, 2);

            p.pop();
        }
    }

    // Draw consciousness particles
    function drawParticles() {
        // Check if particles array is initialized
        if (!particles || particles.length === 0) {
            console.warn('Particles array not initialized, recreating particles');
            createParticles();
            return;
        }

        const particleCount = Math.floor(100 * consciousnessMass);
        const centerX = p.width / 2;
        const centerY = p.height / 2;

        for (let i = 0; i < Math.min(particleCount, particles.length); i++) {
            // Skip if particle is undefined
            if (!particles[i]) continue;

            const particle = particles[i];

            // Update position
            particle.x += Math.cos(particle.angle) * particle.speed;
            particle.y += Math.sin(particle.angle) * particle.speed;

            // Wrap around edges
            if (particle.x < 0) particle.x = p.width;
            if (particle.x > p.width) particle.x = 0;
            if (particle.y < 0) particle.y = p.height;
            if (particle.y > p.height) particle.y = 0;

            // Adjust angle based on distance from center (gravitational effect)
            const dx = centerX - particle.x;
            const dy = centerY - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const gravitationalPull = 0.01 * consciousnessMass / Math.max(distance, 50);

            const targetAngle = Math.atan2(dy, dx);
            particle.angle = p.lerp(particle.angle, targetAngle, gravitationalPull);

            // Draw particle
            p.noStroke();
            p.fill(particle.color);
            p.ellipse(particle.x, particle.y, particle.size);

            // Draw trail
            p.stroke(particle.color);
            p.strokeWeight(1);
            p.line(
                particle.x,
                particle.y,
                particle.x - Math.cos(particle.angle) * particle.speed * 5,
                particle.y - Math.sin(particle.angle) * particle.speed * 5
            );
        }

        // Draw energy field at center
        const energyRadius = 30 + kineticEnergy * 50;

        p.noFill();
        for (let i = 0; i < 3; i++) {
            const alpha = p.map(i, 0, 3, 150, 50);
            p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], alpha);
            p.strokeWeight(2);
            p.ellipse(centerX, centerY, energyRadius * 2 - i * 20);
        }

        // Draw pulsing effect
        const pulseSize = energyRadius + Math.sin(time * 2) * 10;
        p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], 100);
        p.strokeWeight(3);
        p.ellipse(centerX, centerY, pulseSize * 2);
    }

    // Draw background grid
    function drawGrid() {
        p.stroke(config.colors.primary[0], config.colors.primary[1], config.colors.primary[2], 50);
        p.strokeWeight(1);

        const gridSize = 30;
        for (let x = 0; x < p.width; x += gridSize) {
            p.line(x, 0, x, p.height);
        }

        for (let y = 0; y < p.height; y += gridSize) {
            p.line(0, y, p.width, y);
        }
    }

    // Draw comparison view (low vs high velocity)
    function drawComparisonView() {
        // Draw dividing line
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.line(p.width / 2, 0, p.width / 2, p.height);

        // Draw titles
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(16);
        p.text("Low Velocity (v_c = 0.2)", p.width * 0.25, 30);
        p.text("High Velocity (v_c = 0.9)", p.width * 0.75, 30);

        // Draw equations
        p.fill(config.colors.accent);
        p.textSize(20);
        p.text(`K_c = 1.0 × 0.2² / 2 = 0.02`, p.width * 0.25, 60);
        p.text(`K_c = 1.0 × 0.9² / 2 = 0.405`, p.width * 0.75, 60);

        // Draw low velocity side (left)
        drawVelocityScenario(p.width * 0.25, 1.0, 0.2);

        // Draw high velocity side (right)
        drawVelocityScenario(p.width * 0.75, 1.0, 0.9);

        // Draw explanation
        p.fill(config.colors.text);
        p.textSize(14);
        p.textAlign(p.CENTER, p.CENTER);
        p.text("Low kinetic energy", p.width * 0.25, p.height - 40);
        p.text("High kinetic energy", p.width * 0.75, p.height - 40);
    }

    // Draw a velocity scenario for the comparison view
    function drawVelocityScenario(centerX, mass, velocity) {
        const centerY = p.height / 2;
        const radius = 100;

        // Calculate kinetic energy for this scenario
        const energy = mass * (velocity * velocity) / 2;

        // Draw energy field
        p.noFill();
        for (let i = 0; i < 3; i++) {
            const alpha = p.map(i, 0, 3, 150, 50);
            p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], alpha);
            p.strokeWeight(2);
            const energyRadius = 30 + energy * 100;
            p.ellipse(centerX, centerY, energyRadius * 2 - i * 10);
        }

        // Draw particles orbiting center
        const particleCount = 20;
        const particleColor = p.lerpColor(
            p.color(config.colors.lowVelocity),
            p.color(config.colors.highVelocity),
            velocity
        );

        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * p.TWO_PI + time * velocity * 2;
            const distance = radius * 0.7;
            const x = centerX + Math.cos(angle) * distance;
            const y = centerY + Math.sin(angle) * distance;

            // Draw particle
            p.noStroke();
            p.fill(particleColor);
            p.ellipse(x, y, 6 * mass);

            // Draw trail
            p.stroke(particleColor);
            p.strokeWeight(1);
            const trailLength = velocity * 20;
            p.line(
                x,
                y,
                x - Math.cos(angle) * trailLength,
                y - Math.sin(angle) * trailLength
            );
        }

        // Draw velocity vector
        p.stroke(config.colors.text);
        p.strokeWeight(2);
        p.fill(config.colors.text);
        const vectorLength = velocity * 50;
        drawArrow(centerX - vectorLength / 2, centerY + radius * 0.4, centerX + vectorLength / 2, centerY + radius * 0.4);

        // Draw velocity label
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text(`Velocity: ${velocity.toFixed(1)}`, centerX, centerY + radius * 0.4 + 20);

        // Draw energy label
        p.textSize(14);
        p.text(`Energy: ${energy.toFixed(3)}`, centerX, centerY - radius * 0.4);
    }

    // Helper function to draw arrows
    function drawArrow(x1, y1, x2, y2) {
        p.line(x1, y1, x2, y2);

        const angle = p.atan2(y2 - y1, x2 - x1);
        const arrowSize = 10;

        p.push();
        p.translate(x2, y2);
        p.rotate(angle);
        p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
        p.pop();
    }

    // Draw labels and equation
    function drawLabels() {
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);

        // Draw equation at the top
        p.textSize(24);
        p.fill(config.colors.accent);
        p.text(`K_c = m_c × v_c² / 2`, p.width / 2, 30);

        // Draw current values
        p.textSize(16);
        p.fill(config.colors.text);
        p.text(`m_c = ${consciousnessMass.toFixed(1)}   v_c = ${consciousnessVelocity.toFixed(2)}   K_c = ${kineticEnergy.toFixed(3)}`, p.width / 2, 60);

        // Draw center label
        p.textSize(14);
        p.text("Consciousness Field", p.width / 2, p.height / 2 + 40);

        // Draw mass and velocity labels
        p.textSize(14);
        p.fill(config.colors.secondary);
        p.text(`Mass: ${consciousnessMass.toFixed(1)}`, p.width / 4, p.height - 40);
        p.fill(p.lerpColor(
            p.color(config.colors.lowVelocity),
            p.color(config.colors.highVelocity),
            consciousnessVelocity
        ));
        p.text(`Velocity: ${consciousnessVelocity.toFixed(2)}`, p.width * 3 / 4, p.height - 40);
    }

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
    };
};

// Initialize the p5.js sketch
new p5(consciousnessKineticSketch);
